import React, { useState, useEffect, useRef } from "react";
import { Input } from "@component/input";
import {
  SearchIcon,
  XIcon,
} from "lucide-react";
import { JobSearchParams } from "@type/job";

interface Props {
  onReset: () => void;
  onSearch: (params: JobSearchParams, callback: VoidFunction) => void;
}

export function JobSearchComponent(props: Props) {
  const [query, setQuery] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchRef = useRef<HTMLDivElement | null>(null);

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    try {
      const storedSearches = localStorage.getItem("recentSearches");
      if (storedSearches) {
        const parsedSearches = JSON.parse(storedSearches);
        if (Array.isArray(parsedSearches) && parsedSearches.length > 0) {
          setRecentSearches(parsedSearches);
          return;
        }
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = () => {
    setIsOpen(false);
    // Save non-empty search query to localStorage
    if (query.trim()) {
      // Save current query to localStorage
      localStorage.setItem("lastSearchQuery", query.trim());

      // Update recent searches
      try {
        // Create a new array with the current search at the beginning
        const updatedSearches = [query.trim()];

        // Add existing searches, excluding the current one to avoid duplicates
        recentSearches.forEach((search) => {
          if (search !== query.trim() && updatedSearches.length < 5) {
            updatedSearches.push(search);
          }
        });

        // Update state and localStorage
        setRecentSearches(updatedSearches);
        localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    }

    const params: JobSearchParams = {
      keyword: query,
      page: 1, // Adding default page value
      top_k: 10, // Adding default top_k value
      // Add location, job_type etc. based on your UI's filter options
    };
    props.onSearch(params, () => {
      setIsOpen(false)
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      if (selectedIndex < recentSearches.length) {
        setQuery(recentSearches[selectedIndex]);
      }
      handleSearch();
    } else if (e.key === "Enter") {
      handleSearch();
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (e.target.value) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  const clearSearch = () => {
    setQuery("");
    setIsOpen(false);
    props.onReset();
  };

  const handleRecentSearch = (keyword: string) => {
    const params: JobSearchParams = {
      keyword: keyword,
      page: 1, // Adding default page value
      top_k: 10, // Adding default top_k value
      // Add location, job_type etc. based on your UI's filter options
    };
    // Add location, job_type etc. based on your UI's filter options
    props.onSearch(params, () => {
      setQuery(keyword)
      setIsOpen(false)
    });
  };

  return (
    <div className="w-full flex flex-col items-center justify-center mb-4">
      <div className="w-full relative" ref={searchRef}>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />

          <Input
            type="text"
            placeholder="Search for jobs, companies, or positions..."
            className="pl-10 pr-10 h-12 text-base bg-white border-gray-300 text-gray-800 placeholder-gray-500 focus:border-blue-500"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
          />

          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Clear search"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {isOpen && recentSearches.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md shadow-lg z-10">
            <div className="p-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
              Recent Searches
            </div>
            <div>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleRecentSearch(search)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                >
                  <SearchIcon className="h-3 w-3 text-gray-400" />
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
